import { html, LitElement, PropertyValues, TemplateResult } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import {
  endOfDay,
  getDate,
  getMonth,
  getTime,
  getYear,
  startOfDay,
  set,
  format,
  subMonths,
  getUnixTime,
} from 'date-fns';
import { DatePickerCalendarWebComponent } from '../datePicker/datepicker-calendar';
import {
  CalendarEvents,
  DateRangePickerInputs,
  DateRangePickerTranslateStrings,
  FROM_TIMESTAMP_ID,
  TO_TIMESTAMP_ID,
} from '../types';
import { enUS } from 'date-fns/locale';
import { GzInputNumberWebComponent } from '../../gzInput';
import { GzInputWebComponent } from '../../gzInput/component/GzInputWebComponent';
import { utcToZonedTime } from 'date-fns-tz';
import { jsTimestampToUnixTimestamp, unixTimestampToJsTimestamp } from '@nirvana/shared/time';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { AbsoluteTimeRange } from '@nirvana/shared/dtos/types/shared/time';
import { getLocaleObjectFromUserLocale } from 'apps/webcomponents/src/utils/i18n.utils';
import { classMap } from 'lit/directives/class-map.js';

// used this repo as a starting point: https://github.com/frocher/wc-range-datepicker for the range datepicker behavior
@customElement('gz-date-range-picker')
export class GzDateRangePickerWebComponent extends LitElement {
  /**
   * Set locale of the calendar. The default is 'en-US'
   */
  @property({ type: String, reflect: true })
  userLocale = '';

  @property({ type: String, reflect: true })
  timezone = '';

  /**
   * This property is used to store the strings displayed on the component (i.e. buttons and input labels/placeholders).
   * The component doesn't directly translate the strings, these values need to be provided by the developer.
   */
  @property({ type: Object, reflect: true })
  translateStrings: DateRangePickerTranslateStrings = {
    confirmButton: 'PLACEHOLDER_APPLY',
    cancelButton: 'PLACEHOLDER_CANCEL',
    fromLabel: 'PLACEHOLDER_FROM',
    toLabel: 'PLACEHOLDER_TO',
  };

  /**
   * Max date allowed for selection. Format is Unix timestamp.
   * When max is set, the dates after the maximum timestamp will be disabled on the calendar.
   */
  @property({ type: Number })
  max;

  /**
   * Minimal date allowed for selection. Format is Unix timestamp.
   * When min is set, the dates before the minimum timestamp will be disabled on the calendar.
   */
  @property({ type: Number })
  min;

  /**
   * Set month.
   * Default is current month.
   */
  @property({ type: Number })
  month = getMonth(new Date()) + 1;

  /**
   * Set year.
   * Default is current year.
   */
  @property({ type: Number })
  year = getYear(new Date());

  @property({ type: Number, reflect: true })
  fromTimestamp;

  @property({ type: Number, reflect: true })
  toTimestamp;

  /**
   * Setting this to true will display the read-only input for the selected date and number inputs for customizing the
   * hours and minutes in the picker.
   */
  @property({ type: Boolean })
  showTimePickers = false;

  @property({ type: Boolean, reflect: true })
  isRelativeTimeRange = false;

  @property({ type: Boolean, reflect: true })
  isAllToDateSelected = false;

  @property({ type: Boolean, reflect: true })
  displaySecondsInput = false;

  @query('#firstDatePicker')
  firstPicker!: DatePickerCalendarWebComponent;

  @query('#secondDatePicker')
  secondPicker!: DatePickerCalendarWebComponent;

  @query('#fromTimestampDay')
  fromTimestampDay!: GzInputWebComponent;

  @query('#fromTimestampHours')
  fromTimestampHours!: GzInputNumberWebComponent;

  @query('#fromTimestampMinutes')
  fromTimestampMinutes!: GzInputNumberWebComponent;

  @query('#fromTimestampSeconds')
  fromTimestampSeconds!: GzInputNumberWebComponent;

  @query('#toTimestampHours')
  toTimestampHours!: GzInputNumberWebComponent;

  @query('#toTimestampMinutes')
  toTimestampMinutes!: GzInputNumberWebComponent;

  @query('#toTimestampSeconds')
  toTimestampSeconds!: GzInputNumberWebComponent;

  @query('#toTimestampDay')
  toTimestampDay!: GzInputWebComponent;

  /**
   * This property is used to style the days within the selected timerange
   */
  @state()
  private hoveredDate;

  /**
   * This property is used for calculating the month number for the first calendar in the dateRange picker
   */
  @state()
  private monthMinus;

  /**
   * This property is used for calculating the year number for the first calendar in the dateRange picker
   */
  @state()
  private yearMinus;

  /**
   * This will store the locale object for the calendar. It will be dynamically updated based on the 'userLocale'
   * property.
   */
  @state()
  private locale: Locale = enUS;

  @state()
  submitDisabled = true;

  static get styles() {
    return styles;
  }

  constructor() {
    super();
    const now = new Date();
    const lastMonth = subMonths(now, 1);
    this.monthMinus = getMonth(lastMonth) + 1;
    this.yearMinus = getYear(lastMonth);
  }

  render(): TemplateResult {
    return html` <div class="content-wrapper">
      ${this.renderDateRangeInputs()} ${this.renderRangeCalendars()} ${this.renderCalendarActionButtons()}
    </div>`;
  }

  private renderRangeCalendars(): TemplateResult {
    return html`
      <div class="calendar-wrapper">
        <gz-datepicker-calendar
          id="firstDatePicker"
          min="${this.min}"
          max="${this.max}"
          prev
          .hoveredDate="${this.hoveredDate}"
          .dateTo="${this.toTimestamp}"
          .dateFrom="${this.fromTimestamp}"
          .locale="${this.locale}"
          .timezone="${this.timezone}"
          month="${this.monthMinus}"
          year="${this.yearMinus}"
          @prevMonth="${this.handlePrevMonth}"
          @hoveredDateChanged="${this.hoveredDateChanged}"
          @dateFromChanged="${this.dateFromChanged}"
          @dateToChanged="${this.dateToChanged}"
        >
        </gz-datepicker-calendar>
        <gz-datepicker-calendar
          id="secondDatePicker"
          min="${this.min}"
          max="${this.max}"
          next
          .hoveredDate="${this.hoveredDate}"
          .dateTo="${this.toTimestamp}"
          .dateFrom="${this.fromTimestamp}"
          .locale="${this.locale}"
          .timezone="${this.timezone}"
          month="${this.month}"
          year="${this.year}"
          @nextMonth="${this.handleNextMonth}"
          @hoveredDateChanged="${this.hoveredDateChanged}"
          @dateFromChanged="${this.dateFromChanged}"
          @dateToChanged="${this.dateToChanged}"
        >
        </gz-datepicker-calendar>
      </div>
    `;
  }

  private renderCalendarActionButtons(): TemplateResult {
    return html`
      <div class="calendar-actions">
        <gz-button
          type="quiet"
          buttonHeight="small"
          buttonFontSize="small"
          class="footer-item"
          ?disabled="${this.submitDisabled}"
          color="primary"
          @click="${this.handleDateRangeConfirm}"
          >${this.translateStrings.confirmButton}</gz-button
        >
        <gz-button
          buttonHeight="small"
          buttonFontSize="small"
          class="footer-item"
          color="secondary"
          type="quiet"
          @click="${this.handleDateRangeCancel}"
          >${this.translateStrings.cancelButton}</gz-button
        >
      </div>
    `;
  }

  private renderDateRangeInput(label: string, id: string): TemplateResult {
    const inputsClasses = {
      inputs: true,
      compact: this.displaySecondsInput,
    };
    const secondInputClasses = {
      'right-range-input': true,
      'left-range-input': this.displaySecondsInput,
    };
    return html` <div class="date-range-inputs-block">
      <div class="label">${label}</div>
      <div class="${classMap(inputsClasses)}">
        <gz-input readonly id="${id}Day" type="text" inputwidth="120"></gz-input>
        <div class="hour-control" id="${id}">
          <gz-input-number
            min="0"
            max="23"
            circular
            minLength="2"
            placeholder="00"
            id="${id}Hours"
            inputWidth="74"
            class="left-range-input"
            @change="${this.handleTimeInputChange}"
            ?readonly="${this.isAllToDateSelected}"
          ></gz-input-number>
          :
          <gz-input-number
            min="0"
            max="59"
            circular
            minLength="2"
            placeholder="00"
            id="${id}Minutes"
            inputWidth="74"
            class="${classMap(secondInputClasses)}"
            @change="${this.handleTimeInputChange}"
            ?readonly="${this.isAllToDateSelected}"
          ></gz-input-number>
          ${this.renderSecondsInput(id)}
        </div>
      </div>
    </div>`;
  }

  private renderSecondsInput(id: string): TemplateResult | undefined {
    if (!this.displaySecondsInput) {
      return;
    }
    return html`
      :
      <gz-input-number
        min="0"
        max="59"
        circular
        minLength="2"
        placeholder="00"
        id="${id}Seconds"
        inputWidth="74"
        class="right-range-input"
        @change="${this.handleTimeInputChange}"
        ?readonly="${this.isAllToDateSelected}"
      ></gz-input-number>
    `;
  }

  private renderDateRangeInputs(): TemplateResult {
    return html`
      <div class="calendar-date-range-inputs">
        ${this.renderDateRangeInput(this.translateStrings.fromLabel, FROM_TIMESTAMP_ID)}
        ${this.renderDateRangeInput(this.translateStrings.toLabel, TO_TIMESTAMP_ID)}
      </div>
    `;
  }

  updated(properties: PropertyValues): void {
    if (properties.has('month') || properties.has('year')) {
      this.monthChanged(this.month, this.year);
    }
    if (properties.has('userLocale')) {
      this.locale = getLocaleObjectFromUserLocale(this.userLocale);
    }
  }

  public handlePrevMonth(): void {
    this.secondPicker.handlePrevMonth();
  }

  public handleNextMonth(): void {
    this.firstPicker.handleNextMonth();
  }

  private hoveredDateChanged(e: CustomEvent): void {
    this.hoveredDate = e.detail.value;
  }

  private monthChanged(month: number, year: number): void {
    if (year && month) {
      this.monthMinus = month === 1 ? 12 : month - 1;
      if (this.monthMinus === 12) {
        this.yearMinus = year - 1;
      } else {
        this.yearMinus = year;
      }
    }
  }

  private dateToChanged(e: CustomEvent): void {
    this.toTimestamp = e.detail.value;
    this.submitDisabled = isNaN(this.toTimestamp) || isEmpty(this.toTimestamp);

    const dateRangePickerInputs = this.getValuesForDateRangePickerInputs(this.toTimestamp, true, e.detail.isCurrent);
    this.updateTimeRangeInputs('toTimestamp', dateRangePickerInputs);
    this.emitDateRangeChangeEvent(e.detail.value, 'toTimestamp');
  }

  private dateFromChanged(e: CustomEvent): void {
    this.submitDisabled = true;
    this.fromTimestamp = e.detail.value;

    const dateRangePickerInputs = this.getValuesForDateRangePickerInputs(this.fromTimestamp, false);
    this.updateTimeRangeInputs('fromTimestamp', dateRangePickerInputs);
    this.emitDateRangeChangeEvent(e.detail.value, 'fromTimestamp');
  }

  public updateTimeRangeInputs(key: string, values: DateRangePickerInputs) {
    const dayProp = `${key}Day`;
    const hoursProp = `${key}Hours`;
    const minutesProp = `${key}Minutes`;
    const secondsProp = `${key}Seconds`;

    if (this[dayProp] !== null) {
      this[dayProp].value = values.day;
    }
    if (this[hoursProp] !== null) {
      this[hoursProp].value = values.hours;
    }
    if (this[minutesProp] !== null) {
      this[minutesProp].value = values.minutes;
    }
    if (this[secondsProp] !== null) {
      this[secondsProp].value = values.seconds;
    }
  }

  private handleDateRangeConfirm() {
    const selectedInterval: AbsoluteTimeRange | undefined = this.getUpdatedTimeRange();

    this.dispatchEvent(
      new CustomEvent(CalendarEvents.SELECTED_RANGE_CONFIRMED, {
        detail: selectedInterval,
      })
    );
  }

  private handleDateRangeCancel() {
    this.dispatchEvent(
      new CustomEvent(CalendarEvents.SELECTED_RANGE_CANCELLED, {
        bubbles: true,
        composed: true,
      })
    );
  }

  private emitTimeRangeChangeEvent() {
    this.dispatchEvent(
      new CustomEvent(CalendarEvents.CUSTOM_HOURS_CHANGED, {
        bubbles: true,
        composed: true,
      })
    );
  }

  private emitDateRangeChangeEvent(timeStampValue: number, key: string) {
    this.dispatchEvent(
      new CustomEvent(CalendarEvents.CUSTOM_DATE_CHANGED, {
        detail: {
          timeStampValue: timeStampValue,
          key: key,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  /**
   * When the user confirms the selection, compute the FROM and TO timestamps based on the values set in the hours
   * and minutes number inputs.
   */
  private getUpdatedTimeRange(): AbsoluteTimeRange | undefined {
    if (!this.fromTimestamp || !this.toTimestamp) {
      return;
    }

    const startDay = getDate(unixTimestampToJsTimestamp(this.fromTimestamp)); // day of month, i.e. 28, 29
    const endDay = getDate(unixTimestampToJsTimestamp(this.toTimestamp));

    const updatedFromTimestamp = set(new Date(unixTimestampToJsTimestamp(this.fromTimestamp)), {
      date: startDay,
      hours: this.fromTimestampHours.value as number,
      minutes: this.fromTimestampMinutes.value as number,
      seconds: (this.fromTimestampSeconds?.value as number) || 0,
    });

    const updatedToTimestamp = set(new Date(unixTimestampToJsTimestamp(this.toTimestamp)), {
      date: endDay,
      hours: this.toTimestampHours.value as number,
      minutes: this.toTimestampMinutes.value as number,
      seconds: (this.toTimestampSeconds?.value as number) || 0,
    });

    return {
      fromTimestamp: jsTimestampToUnixTimestamp(updatedFromTimestamp.valueOf()),
      toTimestamp: jsTimestampToUnixTimestamp(updatedToTimestamp.valueOf()),
    };
  }

  public updateInputsToValue(fromTimestamp: number, toTimestamp: number) {
    this.updateFromInput(fromTimestamp);
    this.updateToInput(toTimestamp);
  }

  public updateFromInput(fromTimestamp: number) {
    if (fromTimestamp) {
      const fromValues = this.getTimeComponentsFromTimestamp(fromTimestamp);
      this.updateTimeRangeInputs('fromTimestamp', fromValues);
    }
  }

  public updateToInput(toTimestamp: number) {
    if (toTimestamp) {
      const toValues = this.getTimeComponentsFromTimestamp(toTimestamp);
      this.updateTimeRangeInputs('toTimestamp', toValues);
    }
  }

  public validateRange() {
    const getUpdatedTimeRange = this.getUpdatedTimeRange();

    if (!getUpdatedTimeRange?.fromTimestamp || !getUpdatedTimeRange?.toTimestamp) {
      return;
    }

    this.submitDisabled = getUpdatedTimeRange?.fromTimestamp < this.min || getUpdatedTimeRange?.toTimestamp > this.max;
  }

  private getTimeComponentsFromTimestamp(timestamp: number): {
    day: string;
    hours: number;
    minutes: number;
    seconds: number;
  } {
    const date = new Date(timestamp * 1000);
    return {
      day: format(date, 'd MMM yyyy'),
      hours: date.getHours(),
      minutes: date.getMinutes(),
      seconds: date.getSeconds(),
    };
  }

  private handleTimeInputChange(event: CustomEvent) {
    this.emitTimeRangeChangeEvent();
  }

  public getValuesForDateRangePickerInputs(
    timestamp: number,
    isEndDate: boolean,
    isCurrent = false
  ): DateRangePickerInputs {
    let dateRangePickerInputs = {
      day: '',
      hours: 0,
      minutes: 0,
      seconds: 0,
    };

    if (timestamp) {
      let timeOfDay;

      if (isEndDate) {
        // for the end range picker, if the user selects the current date set toTimestamp equal to the current time
        timeOfDay =
          isCurrent && !this.isRelativeTimeRange
            ? getTime(utcToZonedTime(Date.now(), this.timezone))
            : getTime(endOfDay(timestamp * 1000));
      } else {
        timeOfDay = getTime(startOfDay(timestamp * 1000));
      }

      dateRangePickerInputs = {
        day: format(timeOfDay, 'd MMM yyyy', { locale: this.locale }),
        hours: parseInt(format(timeOfDay, 'H'), 10),
        minutes: parseInt(format(timeOfDay, 'm'), 10),
        seconds: parseInt(format(timeOfDay, 's'), 10),
      };
    }

    return dateRangePickerInputs;
  }

  public clearSelectedInterval() {
    this.fromTimestamp = undefined;
    this.toTimestamp = undefined;
    const inputsValues: DateRangePickerInputs = {
      day: '',
      minutes: undefined,
      hours: undefined,
      seconds: undefined,
    };
    this.updateTimeRangeInputs('fromTimestamp', inputsValues);
    this.updateTimeRangeInputs('toTimestamp', inputsValues);
  }

  public resetDisplayedMonths() {
    const lastMonth = this.monthMinus < 10 ? '0' + this.monthMinus.toString() : this.monthMinus.toString();
    const currentMonth = this.month < 10 ? '0' + this.month.toString() : this.month.toString();

    this.firstPicker.navigateToMonthAndYear(lastMonth, this.yearMinus.toString());
    this.secondPicker.navigateToMonthAndYear(currentMonth, this.year.toString());
  }
}
